import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAsk } from '../components/Ask';
import { useSession } from '../lib/session';
import { Modal } from '../components/Modal';
import { DateCell } from '../components/DateCell';
import { DocumentViewer } from '../components/DocumentViewer';
import { formatDate, todayValue } from '../lib/dates';
import type { ProjectDocument, RiskCategory, RiskRegisterData, RiskRow, RiskSeverity, RiskStatus } from '../lib/types';
import { RISK_CATEGORY_LABELS, RISK_STATUS_LABELS } from '../lib/types';
import { Linkified, LinksIn } from '../components/Linkified';

const SEVERITY_LABELS: Record<RiskSeverity, string> = { high: 'High', medium: 'Medium', low: 'Low' };
const LIKELIHOOD = ['', 'Rare', 'Unlikely', 'Possible', 'Likely', 'Almost certain'];
const IMPACT = ['', 'Negligible', 'Minor', 'Moderate', 'Major', 'Severe'];
const scoreBand = (score: number): RiskSeverity => (score >= 15 ? 'high' : score >= 8 ? 'medium' : 'low');

/**
 * THE RISK REGISTER, on the company overview — management's one place for risk.
 *
 * Raise a risk against the company or one of its projects; score it on the usual five-by-five
 * (likelihood × impact, high from 15); give it an owner and a review date; plan the response
 * as steps with their own owners and dates; keep the documents that go with it; and close it
 * with what happened. The heat map at the top is the same grid the board deck draws.
 */
export function RiskRegister({ companyId }: { companyId: string }) {
  const [data, setData] = useState<RiskRegisterData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [raising, setRaising] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [cell, setCell] = useState<{ l: number; i: number } | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<RiskRegisterData>(`/companies/${companyId}/risks`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the risk register');
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && !data) return <div className="callout">{error}</div>;
  if (!data) return <div className="empty">Loading…</div>;

  const open = data.risks.filter((r) => r.status !== 'closed');
  const shown = data.risks
    .filter((r) => showClosed || r.status !== 'closed')
    .filter((r) => !cell || (r.likelihood === cell.l && r.impact === cell.i && r.status !== 'closed'));
  const current = data.risks.find((r) => r.id === openId) ?? null;
  const today = todayValue();

  return (
    <>
      <div className="stat-row mb-2">
        <div className="stat">
          <div className="stat-label">Open risks</div>
          <div className="stat-value">{data.summary.open}</div>
        </div>
        <div className="stat">
          <div className="stat-label">High</div>
          <div className="stat-value" style={data.summary.high ? { color: 'var(--dash-bad)' } : undefined}>
            {data.summary.high}
          </div>
        </div>
        <div className="stat">
          <div className="stat-label">Medium</div>
          <div className="stat-value">{data.summary.medium}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Nobody owns it</div>
          <div className="stat-value" style={data.summary.unowned ? { color: 'var(--dash-bad)' } : undefined}>
            {data.summary.unowned}
          </div>
        </div>
        <div className="stat">
          <div className="stat-label">Due for review</div>
          <div className="stat-value">{data.summary.reviewDue}</div>
        </div>
      </div>

      <div className="risk-top mb-2">
        <section className="card dash-card">
          <h3>Heat map</h3>
          <HeatMap risks={open} selected={cell} onSelect={setCell} />
          <p className="dim dash-note">Likelihood × impact. Click a square to list just those risks.</p>
        </section>

        <section className="card" style={{ margin: 0 }}>
          <div className="card-head">
            <h2>Risk register</h2>
            <span className="row" style={{ gap: 8 }}>
              {cell && (
                <button type="button" className="filter-chip" onClick={() => setCell(null)}>
                  Likelihood {cell.l} × impact {cell.i} <span aria-hidden>×</span>
                </button>
              )}
              {data.summary.closed > 0 && (
                <button type="button" className="btn btn-quiet btn-sm" onClick={() => setShowClosed(!showClosed)}>
                  {showClosed ? 'Hide closed' : `Show ${data.summary.closed} closed`}
                </button>
              )}
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setRaising(true)}>
                Raise a risk
              </button>
            </span>
          </div>
          {shown.length === 0 ? (
            <div className="empty">
              <h3>{data.risks.length === 0 ? 'No risks raised yet' : 'Nothing here'}</h3>
              <p>
                {data.risks.length === 0
                  ? 'Raise the first one: what could go wrong, how likely, how bad, and who owns it.'
                  : 'No open risk matches.'}
              </p>
            </div>
          ) : (
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th>Risk</th>
                    <th>Score</th>
                    <th>Owner</th>
                    <th>Plan</th>
                    <th>Review</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const done = r.mitigations.filter((m) => m.done).length;
                    return (
                      <tr key={r.id} className="clickable" onClick={() => setOpenId(r.id)}>
                        <td>
                          <span className="key">{r.reference}</span> <strong>{r.title}</strong>
                          <div className="dim" style={{ fontSize: 11 }}>
                            {RISK_CATEGORY_LABELS[r.category]} · {r.project ? r.project.name : 'The whole account'}
                            {r.documents.length ? ` · ${r.documents.length} document${r.documents.length === 1 ? '' : 's'}` : ''}
                          </div>
                        </td>
                        <td>
                          <span className={`risk-score sev-${r.severity}`}>{r.score}</span>
                        </td>
                        <td>{r.owner?.name ?? <span style={{ color: 'var(--dash-bad)' }}>Nobody</span>}</td>
                        <td className="muted" style={{ fontSize: 12 }}>
                          {r.mitigations.length === 0 ? (
                            <span style={{ color: r.status === 'closed' ? undefined : 'var(--dash-bad)' }}>No plan yet</span>
                          ) : (
                            `${done} of ${r.mitigations.length} done`
                          )}
                        </td>
                        <td className="mono" style={r.reviewOn && r.reviewOn <= today && r.status !== 'closed' ? { color: 'var(--dash-bad)' } : undefined}>
                          {r.reviewOn ? formatDate(r.reviewOn) : '—'}
                        </td>
                        <td>
                          <span className={r.status === 'closed' ? 'badge' : 'badge badge-info'}>{RISK_STATUS_LABELS[r.status]}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {raising && (
        <RaiseRisk
          data={data}
          companyId={companyId}
          onClose={() => setRaising(false)}
          onRaised={async (id) => {
            setRaising(false);
            await load();
            setOpenId(id);
          }}
        />
      )}
      {current && <RiskDrawer risk={current} data={data} onClose={() => setOpenId(null)} onChanged={load} />}
    </>
  );
}

function HeatMap({
  risks,
  selected,
  onSelect,
}: {
  risks: RiskRow[];
  selected: { l: number; i: number } | null;
  onSelect: (cell: { l: number; i: number } | null) => void;
}) {
  return (
    <div className="heat">
      <div className="heat-axis-y">Likelihood</div>
      <div className="heat-grid">
        {[5, 4, 3, 2, 1].map((l) =>
          [1, 2, 3, 4, 5].map((i) => {
            const count = risks.filter((r) => r.likelihood === l && r.impact === i).length;
            const on = selected?.l === l && selected?.i === i;
            return (
              <button
                key={`${l}-${i}`}
                type="button"
                className={`heat-cell sev-bg-${scoreBand(l * i)}${on ? ' is-on' : ''}`}
                title={`${LIKELIHOOD[l]} × ${IMPACT[i]}: ${count} risk${count === 1 ? '' : 's'}`}
                onClick={() => onSelect(on || count === 0 ? null : { l, i })}
              >
                {count || ''}
              </button>
            );
          }),
        )}
      </div>
      <div className="heat-axis-x">Impact</div>
    </div>
  );
}

function ScorePicker({
  label,
  names,
  value,
  onChange,
}: {
  label: string;
  names: string[];
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="field" style={{ marginBottom: 10 }}>
      <span className="field-label">{label}</span>
      <div className="row wrap" style={{ gap: 4 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className={value === n ? 'chip-toggle is-on' : 'chip-toggle'}
            aria-pressed={value === n}
            onClick={() => onChange(n)}
            title={names[n]}
          >
            {n} · {names[n]}
          </button>
        ))}
      </div>
    </div>
  );
}

function RaiseRisk({
  data,
  companyId,
  onClose,
  onRaised,
}: {
  data: RiskRegisterData;
  companyId: string;
  onClose: () => void;
  onRaised: (id: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState('');
  const [category, setCategory] = useState<RiskCategory>('delivery');
  const [likelihood, setLikelihood] = useState(3);
  const [impact, setImpact] = useState(3);
  const [ownerId, setOwnerId] = useState('');
  const [reviewOn, setReviewOn] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const score = likelihood * impact;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const raised = await api.post<{ id: string }>(`/companies/${companyId}/risks`, {
        title: title.trim(),
        description: description.trim() || undefined,
        projectId: projectId || undefined,
        category,
        likelihood,
        impact,
        ownerId: ownerId || undefined,
        reviewOn: reviewOn ?? undefined,
      });
      onRaised(raised.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not raise the risk');
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Raise a risk"
      subtitle="What could go wrong, how likely it is, how bad it would be, and who owns it."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={busy || title.trim().length < 3} onClick={() => void save()}>
            {busy ? 'Raising…' : 'Raise it'}
          </button>
        </>
      }
    >
      {error && <div className="callout mb-2">{error}</div>}
      <label className="field">
        <span className="field-label">The risk</span>
        <input value={title} maxLength={300} placeholder="Key developer may leave before go-live" onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className="field">
        <span className="field-label">More about it (optional)</span>
        <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <div className="row wrap" style={{ gap: 10 }}>
        <label className="field grow" style={{ minWidth: 200 }}>
          <span className="field-label">Touches</span>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">The whole account</option>
            {data.projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ minWidth: 160 }}>
          <span className="field-label">Kind</span>
          <select value={category} onChange={(e) => setCategory(e.target.value as RiskCategory)}>
            {(Object.keys(RISK_CATEGORY_LABELS) as RiskCategory[]).map((c) => (
              <option key={c} value={c}>
                {RISK_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <ScorePicker label="Likelihood" names={LIKELIHOOD} value={likelihood} onChange={setLikelihood} />
      <ScorePicker label="Impact" names={IMPACT} value={impact} onChange={setImpact} />
      <p style={{ margin: '0 0 12px' }}>
        Score <span className={`risk-score sev-${scoreBand(score)}`}>{score}</span>{' '}
        <span className="dim">{SEVERITY_LABELS[scoreBand(score)]}</span>
      </p>
      <div className="row wrap" style={{ gap: 10 }}>
        <label className="field grow" style={{ minWidth: 200 }}>
          <span className="field-label">Owner</span>
          <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
            <option value="">Nobody yet</option>
            {data.owners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <div className="field" style={{ width: 170 }}>
          <span className="field-label">Review on</span>
          <DateCell value={reviewOn} editable min={todayValue()} onChange={setReviewOn} emptyLabel="No date" />
        </div>
      </div>
    </Modal>
  );
}

/** One risk, edited in place: its scoring, its plan, its documents, and closing it. */
function RiskDrawer({
  risk,
  data,
  onClose,
  onChanged,
}: {
  risk: RiskRow;
  data: RiskRegisterData;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const ask = useAsk();
  const { session } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const closed = risk.status === 'closed';

  async function act(work: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await work();
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not save');
    } finally {
      setBusy(false);
    }
  }
  const patch = (body: Record<string, unknown>) => act(() => api.patch(`/risks/${risk.id}`, body));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()} aria-label={`${risk.reference} ${risk.title}`}>
        <div className="drawer-head">
          <div className="row-between">
            <span className="key">{risk.reference}</span>
            <button type="button" className="btn btn-quiet btn-sm" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
          <EditableTitle value={risk.title} disabled={closed || busy} onSave={(title) => patch({ title })} />
          <div className="row wrap" style={{ gap: 8, marginTop: 6 }}>
            <span className={`risk-score sev-${risk.severity}`}>{risk.score}</span>
            <span className="dim" style={{ fontSize: 12 }}>
              {SEVERITY_LABELS[risk.severity]} · raised {formatDate(risk.raisedAt.slice(0, 10))}
              {risk.raisedBy ? ` by ${risk.raisedBy}` : ''}
            </span>
          </div>
        </div>

        <div className="card-pad stack">
          {error && <div className="callout">{error}</div>}
          {closed && (
            <div className="callout callout-ok">
              Closed {risk.closedAt ? formatDate(risk.closedAt.slice(0, 10)) : ''}
              {risk.closedBy ? ` by ${risk.closedBy}` : ''}: <Linkified text={risk.outcome} />
            </div>
          )}

          <label className="field" style={{ marginBottom: 0 }}>
            <span className="field-label">More about it</span>
            <BlurText value={risk.description} disabled={closed || busy} onSave={(description) => patch({ description })} />
            <LinksIn text={risk.description} />
          </label>

          <div className="row wrap" style={{ gap: 10 }}>
            <label className="field grow" style={{ minWidth: 180, marginBottom: 0 }}>
              <span className="field-label">Touches</span>
              <select value={risk.project?.id ?? ''} disabled={closed || busy} onChange={(e) => void patch({ projectId: e.target.value || null })}>
                <option value="">The whole account</option>
                {data.projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field" style={{ minWidth: 150, marginBottom: 0 }}>
              <span className="field-label">Kind</span>
              <select value={risk.category} disabled={closed || busy} onChange={(e) => void patch({ category: e.target.value })}>
                {(Object.keys(RISK_CATEGORY_LABELS) as RiskCategory[]).map((c) => (
                  <option key={c} value={c}>
                    {RISK_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {!closed && (
            <>
              <ScorePicker label="Likelihood" names={LIKELIHOOD} value={risk.likelihood} onChange={(likelihood) => void patch({ likelihood })} />
              <ScorePicker label="Impact" names={IMPACT} value={risk.impact} onChange={(impact) => void patch({ impact })} />
            </>
          )}

          <div className="row wrap" style={{ gap: 10 }}>
            <label className="field grow" style={{ minWidth: 180, marginBottom: 0 }}>
              <span className="field-label">Owner</span>
              <select value={risk.owner?.id ?? ''} disabled={closed || busy} onChange={(e) => void patch({ ownerId: e.target.value || null })}>
                <option value="">Nobody yet</option>
                {data.owners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="field" style={{ width: 160, marginBottom: 0 }}>
              <span className="field-label">Review on</span>
              <DateCell value={risk.reviewOn} editable={!closed && !busy} onChange={(reviewOn) => void patch({ reviewOn })} emptyLabel="No date" />
            </div>
            {!closed && (
              <label className="field" style={{ width: 170, marginBottom: 0 }}>
                <span className="field-label">Status</span>
                <select value={risk.status} disabled={busy} onChange={(e) => void patch({ status: e.target.value as RiskStatus })}>
                  {(['open', 'mitigating', 'accepted'] as RiskStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {RISK_STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <Plan risk={risk} data={data} disabled={closed || busy} act={act} />
          <Documents risk={risk} act={act} ask={ask} />

          <div className="row wrap" style={{ gap: 8, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
            {closed ? (
              <button type="button" className="btn" disabled={busy} onClick={() => void act(() => api.post(`/risks/${risk.id}/reopen`))}>
                Reopen
              </button>
            ) : (
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={async () => {
                  const outcome = await ask.prompt({
                    title: `Close ${risk.reference}?`,
                    body: 'Say what happened in the end — it passed, it happened and was handled, it moved on.',
                    label: 'Outcome',
                    minLength: 3,
                    multiline: true,
                    confirmLabel: 'Close the risk',
                  });
                  if (outcome) await act(() => api.post(`/risks/${risk.id}/close`, { outcome }));
                }}
              >
                Close with an outcome
              </button>
            )}
            {session?.person.isAdmin && (
            <button
              type="button"
              className="btn btn-quiet"
              disabled={busy}
              title="Administrators only — for a risk raised by mistake"
              onClick={async () => {
                if (await ask.confirm({ title: `Delete ${risk.reference}?`, body: 'Only for a risk raised by mistake. A real risk is closed with an outcome instead.', danger: true, confirmLabel: 'Delete' })) {
                  await act(() => api.del(`/risks/${risk.id}`));
                  onClose();
                }
              }}
            >
              Delete
            </button>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

function EditableTitle({ value, disabled, onSave }: { value: string; disabled: boolean; onSave: (v: string) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <input
      className="risk-title-input"
      value={draft}
      disabled={disabled}
      maxLength={300}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => draft.trim().length >= 3 && draft.trim() !== value && onSave(draft.trim())}
    />
  );
}

function BlurText({ value, disabled, onSave }: { value: string; disabled: boolean; onSave: (v: string) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <textarea
      rows={3}
      value={draft}
      disabled={disabled}
      placeholder="What could happen, and what it would mean."
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => draft !== value && onSave(draft)}
    />
  );
}

/** The response, as steps — each with somebody on it and a date. */
function Plan({
  risk,
  data,
  disabled,
  act,
}: {
  risk: RiskRow;
  data: RiskRegisterData;
  disabled: boolean;
  act: (work: () => Promise<unknown>) => Promise<void>;
}) {
  const [action, setAction] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [dueOn, setDueOn] = useState<string | null>(null);
  const today = todayValue();

  return (
    <div>
      <div className="field-label" style={{ marginBottom: 6 }}>
        Mitigation plan {risk.mitigations.length ? `· ${risk.mitigations.filter((m) => m.done).length} of ${risk.mitigations.length} done` : ''}
      </div>
      {risk.mitigations.length === 0 && <p className="dim" style={{ margin: '0 0 8px', fontSize: 13 }}>No steps yet. What will reduce it, who does it, and by when?</p>}
      <div className="stack-sm">
        {risk.mitigations.map((m) => (
          <div key={m.id} className="risk-step">
            <input
              type="checkbox"
              checked={m.done}
              disabled={disabled}
              onChange={() => void act(() => api.patch(`/risks/${risk.id}/mitigations/${m.id}`, { done: !m.done }))}
              aria-label={m.done ? 'Mark not done' : 'Mark done'}
            />
            <span className={m.done ? 'grow is-done' : 'grow'}>
              <Linkified text={m.action} />
              <span className="dim" style={{ fontSize: 11 }}>
                {' '}
                · {m.owner?.name ?? 'nobody'}
                {m.dueOn ? (
                  <span style={!m.done && m.dueOn < today ? { color: 'var(--dash-bad)' } : undefined}> · by {formatDate(m.dueOn)}</span>
                ) : null}
              </span>
            </span>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              disabled={disabled}
              aria-label="Remove step"
              onClick={() => void act(() => api.del(`/risks/${risk.id}/mitigations/${m.id}`))}
            >
              ×
            </button>
          </div>
        ))}
      </div>
      {!disabled && (
        <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
          <input className="grow" style={{ minWidth: 180 }} value={action} placeholder="Add a step" maxLength={300} onChange={(e) => setAction(e.target.value)} />
          <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} style={{ width: 'auto' }} aria-label="Who">
            <option value="">Who?</option>
            {data.owners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <div style={{ width: 140 }}>
            <DateCell value={dueOn} editable onChange={setDueOn} emptyLabel="By when?" />
          </div>
          <button
            type="button"
            className="btn btn-sm"
            disabled={action.trim().length < 2}
            onClick={() =>
              void act(async () => {
                await api.post(`/risks/${risk.id}/mitigations`, { action: action.trim(), ownerId: ownerId || undefined, dueOn: dueOn ?? undefined });
                setAction('');
                setOwnerId('');
                setDueOn(null);
              })
            }
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}

/** Files kept with the risk: uploaded here, opened in the browser where the format allows. */
function Documents({
  risk,
  act,
  ask,
}: {
  risk: RiskRow;
  act: (work: () => Promise<unknown>) => Promise<void>;
  ask: ReturnType<typeof useAsk>;
}) {
  const picker = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const upload = (file: File | null) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    form.append('title', file.name.replace(/\.[^.]+$/, ''));
    void act(() => api.upload(`/risks/${risk.id}/documents`, form)).then(() => {
      if (picker.current) picker.current.value = '';
    });
  };
  const [viewing, setViewing] = useState<RiskRow['documents'][number] | null>(null);

  return (
    <div>
      <div className="field-label" style={{ marginBottom: 6 }}>
        Documents
      </div>
      {risk.documents.map((d) => (
        <div key={d.id} className="doc-row">
          <span className="grow" style={{ minWidth: 0 }}>
            <strong>{d.title}</strong>
            <span className="doc-meta">
              {d.originalName} · {Math.max(1, Math.round(d.sizeBytes / 1024))} KB{d.uploadedBy ? ` · ${d.uploadedBy}` : ''}
            </span>
          </span>
          {d.viewable && (
            <button type="button" className="btn btn-quiet btn-sm" onClick={() => setViewing(d)}>
              Open
            </button>
          )}
          <a className="btn btn-quiet btn-sm" href={`/api/risk-documents/${d.id}/download`}>
            Download
          </a>
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            onClick={async () => {
              if (await ask.confirm({ title: `Remove “${d.title}”?`, danger: true, confirmLabel: 'Remove' })) {
                await act(() => api.del(`/risk-documents/${d.id}`));
              }
            }}
          >
            Delete
          </button>
        </div>
      ))}
      <label
        className={`file-drop${dragging ? ' file-drop-over' : ''}`}
        style={{ marginTop: 8 }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          upload(e.dataTransfer.files?.[0] ?? null);
        }}
      >
        <input ref={picker} type="file" className="file-drop-input" onChange={(e) => upload(e.target.files?.[0] ?? null)} />
        <span className="btn btn-primary btn-sm file-drop-button">Attach a document</span>
        <span className="dim">or drag it here — a risk assessment, a letter, the evidence</span>
      </label>

      {/* The same viewer as a project's documents: PDFs and images as they are, Office files as a preview. */}
      {viewing && (
        <DocumentViewer
          route="risk-documents"
          onClose={() => setViewing(null)}
          document={
            {
              id: viewing.id,
              title: viewing.title,
              originalName: viewing.originalName,
              mimeType: viewing.mimeType,
              viewable: viewing.viewable,
              uploadedBy: viewing.uploadedBy ? { name: viewing.uploadedBy } : null,
              signedOn: null,
            } as unknown as ProjectDocument
          }
        />
      )}
    </div>
  );
}
